import * as React from 'react';

import Input, { InputProps } from '../../atoms/Input';

const WEIGHT_REGEX = /\D*(\d+|\d.*?\d)(?:,(\d{1,3}))?\D*$/;

export interface Props extends Omit<InputProps, 'type'> {
  value?: string;
  unit?: '' | 'kilogram' | 'gram';
  decimalPrecision?: number;
  allowsDecimal?: boolean;
}

const WeightInput = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      className,
      unit = 'kilogram',
      value,
      allowsDecimal = false,
      decimalPrecision = 1,
      onChange,
      onFocus,
      ...props
    },
    ref,
  ) => {
    const innerRef = React.useRef<HTMLInputElement>(null);
    const inputRef = (ref as React.RefObject<HTMLInputElement>) ?? innerRef;

    /**
     * Formats weight with Intl
     */
    const formatWeight = React.useCallback(
      (textValue: number | '') => {
        if (!textValue && textValue !== 0) return '';
        if (Number.isNaN(textValue)) return '';

        return new Intl.NumberFormat('pt-BR', {
          style: 'unit',
          unit,
          minimumFractionDigits: allowsDecimal ? decimalPrecision : 0,
          maximumFractionDigits: allowsDecimal ? decimalPrecision : 0,
        }).format(textValue);
      },
      [allowsDecimal, decimalPrecision, unit],
    );

    const [displayValue, setDisplayValue] = React.useState(
      formatWeight(value ? parseFloat(value) : ''),
    );

    /**
     * Formats weight and updates cursor position
     */
    const handleChange = React.useCallback<NonNullable<InputProps['onChange']>>(
      (e) => {
        const weightParts = WEIGHT_REGEX.exec(e.target.value);
        const rawValue = weightParts
          ? parseFloat(
              `${weightParts[1].replaceAll(/\D/g, '')}.${
                allowsDecimal
                  ? weightParts[2]?.slice(0, decimalPrecision) || '00'
                  : '00'
              }`,
            )
          : '';
        const formattedValue = formatWeight(rawValue);

        // Cursor
        const beforeCursor = e.target.value.slice(
          0,
          e.target.selectionStart || undefined,
        );
        const digitsBeforeCursor = beforeCursor.replace(/[^\d]/g, '').length;

        const nativeEvent = e.nativeEvent as InputEvent;

        // Updates display/onChange
        setDisplayValue(formattedValue);
        e.target.value = rawValue !== '' ? String(rawValue) : '';
        onChange?.(e);

        const justTypedComma =
          nativeEvent.inputType === 'insertText' && nativeEvent.data === ',';

        // Set Caret position
        window.requestAnimationFrame(() => {
          const inputEl = inputRef.current;
          if (!inputEl) return;

          let newCursor = 0;

          if (justTypedComma && formattedValue.includes(',')) {
            // Move cursor to the first digit after the comma
            newCursor = formattedValue.indexOf(',') + 1;
          } else {
            let digitCount = 0;
            let lastDigitPosition = 0;
            for (let i = 0; i < formattedValue.length; i += 1) {
              if (/\d/.test(formattedValue[i])) {
                digitCount += 1;
                lastDigitPosition = i;
              }
              if (digitCount === digitsBeforeCursor) {
                newCursor = i + 1;
                break;
              } else if (i === formattedValue.length - 1) {
                newCursor = lastDigitPosition + 1;
              }
            }
          }
          inputEl.setSelectionRange(newCursor, newCursor);
        });
      },
      [allowsDecimal, decimalPrecision, formatWeight, inputRef, onChange],
    );

    // Keep state in sync with external value
    React.useEffect(() => {
      setDisplayValue(formatWeight(value ? parseFloat(value) : ''));
    }, [formatWeight, value]);

    return (
      <Input
        className={className}
        ref={inputRef}
        type="text"
        value={displayValue}
        onChange={handleChange}
        {...props}
      />
    );
  },
);

export default WeightInput;
